/**
 * Campaign list + create screen (task 20.3 / task 18.2).
 *
 * Lists the player's campaigns, links into each (play view / green room), and
 * hosts a create-campaign form.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { CampaignState, CharacterLedger } from "@rufus/shared-types";
import { useServices } from "../AppContext.js";
import { friendlyErrorMessage, type CreateCampaignRequest } from "../api-client.js";

export function CampaignListScreen() {
  const { api } = useServices();
  const [campaigns, setCampaigns] = useState<readonly CampaignState[] | null>(null);
  // The creator brings a character into the new campaign, so we need their
  // roster to offer a picker (the server requires `creatorCharacterId`).
  const [characters, setCharacters] = useState<readonly CharacterLedger[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [advancementMode, setAdvancementMode] =
    useState<CreateCampaignRequest["advancementMode"]>("milestone");
  const [creatorCharacterId, setCreatorCharacterId] = useState("");
  // Optional AI party members the creator wants Rufus to build & run (R11.5).
  // Each entry is a free-text descriptor; blanks are dropped before submit.
  const [aiPcDescriptors, setAiPcDescriptors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const addAiPc = () => setAiPcDescriptors((prev) => [...prev, ""]);
  const removeAiPc = (i: number) =>
    setAiPcDescriptors((prev) => prev.filter((_, idx) => idx !== i));
  const setAiPcAt = (i: number, value: string) =>
    setAiPcDescriptors((prev) => prev.map((d, idx) => (idx === i ? value : d)));

  const load = () => {
    api
      .listCampaigns()
      .then(setCampaigns)
      .catch((e) => setError(friendlyErrorMessage(e, "Failed to load campaigns.")));
    api
      .listCharacters()
      .then((roster) => {
        setCharacters(roster);
        // Default the picker to the first character so a create is one step.
        setCreatorCharacterId((prev) => prev || roster[0]?.characterId || "");
      })
      .catch((e) => setError(friendlyErrorMessage(e, "Failed to load characters.")));
  };

  useEffect(load, [api]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Only send descriptors the user actually filled in; an empty list means
      // "no AI party members" and the field is omitted entirely.
      const aiPcRequests = aiPcDescriptors
        .map((d) => d.trim())
        .filter((d) => d.length > 0)
        .map((descriptor) => ({ descriptor }));
      await api.createCampaign({
        name,
        advancementMode,
        creatorCharacterId,
        ...(aiPcRequests.length > 0 ? { aiPcRequests } : {}),
      });
      setName("");
      setAdvancementMode("milestone");
      setAiPcDescriptors([]);
      load();
    } catch (err) {
      setError(friendlyErrorMessage(err, "Create failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Campaigns">
      <h1>Campaigns</h1>
      {error ? <p role="alert">{error}</p> : null}

      {campaigns === null ? (
        <p>Loading campaigns…</p>
      ) : campaigns.length === 0 ? (
        <p>No campaigns yet.</p>
      ) : (
        <ul data-testid="campaigns">
          {campaigns.map((c) => {
            // Prefer the player-chosen display name; fall back to the id for
            // campaigns created before the name was persisted (schema back-compat).
            const label = c.name ?? c.campaignId;
            return (
              <li key={c.campaignId}>
                <Link
                  to={`/campaigns/${c.campaignId}/play`}
                  aria-label={`Play campaign ${label}`}
                >
                  {label}
                </Link>{" "}
                <Link
                  to={`/campaigns/${c.campaignId}/green-room`}
                  aria-label={`Green Room for campaign ${label}`}
                >
                  Green Room
                </Link>{" "}
                <Link
                  to={`/campaigns/${c.campaignId}/join`}
                  aria-label={`Bring a character into campaign ${label}`}
                >
                  Bring in a character
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <h2>Create a campaign</h2>
      {characters.length === 0 ? (
        <p>
          You need a character before you can create a campaign.{" "}
          <Link to="/characters/new">Create one</Link> first.
        </p>
      ) : (
        <form onSubmit={create}>
          <label htmlFor="campaign-name">Name</label>
          <input
            id="campaign-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <label htmlFor="advancement">Advancement</label>
          <select
            id="advancement"
            value={advancementMode}
            onChange={(e) =>
              setAdvancementMode(e.target.value as "xp" | "milestone")
            }
          >
            <option value="milestone">Milestone</option>
            <option value="xp">XP</option>
          </select>
          <label htmlFor="creator-character">Bring in character</label>
          <select
            id="creator-character"
            value={creatorCharacterId}
            onChange={(e) => setCreatorCharacterId(e.target.value)}
            required
          >
            {characters.map((c) => (
              <option key={c.characterId} value={c.characterId}>
                {c.name}
              </option>
            ))}
          </select>

          <fieldset>
            <legend>AI players (optional)</legend>
            <p>
              Add AI party members for Rufus to build and run. Describe each one
              (e.g. &ldquo;a gruff dwarf cleric&rdquo;), or leave empty for none.
            </p>
            {aiPcDescriptors.length === 0 ? (
              <p>No AI players.</p>
            ) : (
              <ul data-testid="ai-pc-list">
                {aiPcDescriptors.map((descriptor, i) => (
                  <li key={i}>
                    <label htmlFor={`ai-pc-${i}`}>AI player {i + 1}</label>
                    <input
                      id={`ai-pc-${i}`}
                      value={descriptor}
                      placeholder="Describe this AI companion"
                      onChange={(e) => setAiPcAt(i, e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeAiPc(i)}
                      aria-label={`Remove AI player ${i + 1}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" onClick={addAiPc}>
              Add AI player
            </button>
          </fieldset>

          <button type="submit" disabled={busy || !creatorCharacterId}>
            {busy ? "Creating…" : "Create campaign"}
          </button>
        </form>
      )}
    </section>
  );
}
